import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ApiRequestError } from "./lib/api";
import i18next from "./lib/i18n";
import { createAppRouter } from "./router";
import "./styles.css";
import { toast } from "./ui/toast";

// After a deploy an open panel still runs the old code, whose lazily loaded parts (e.g. the
// action page) are gone from the server: loading one fails. Reload to get the new version
// (Vite's documented way); the data comes back from the server, nothing typed is in those parts.
// Not without network (a reload would replace the panel and its alarms with the browser's
// offline page) and at most once in a few minutes, so a failing part cannot loop (review).
const RELOAD_KEY = "ratun:preload-reload-at";
const RELOAD_EVERY_MS = 5 * 60_000;
window.addEventListener("vite:preloadError", (event) => {
  if (!navigator.onLine) return;
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch (e) {
    // Storage blocked: reload anyway, the deploy case matters more than a loop guard.
    console.warn("reload guard unavailable", e);
  }
  if (Date.now() - last < RELOAD_EVERY_MS) return;
  event.preventDefault();
  window.location.reload();
});

// Session problems are handled in one place: 401 -> login, mfa_required -> MFA setup.
// Other mutation errors become a toast so no failure is silent.
function handleError(error: Error, notify: boolean) {
  if (error instanceof ApiRequestError) {
    if (error.status === 401) return void router.navigate({ to: "/login" });
    if (error.code === "mfa_required") return void router.navigate({ to: "/mfa" });
  }
  if (notify) toast(`${i18next.t("common.error")}: ${error.message}`, "error");
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (error) => handleError(error, false) }),
  mutationCache: new MutationCache({ onError: (error) => handleError(error, true) }),
  defaultOptions: {
    queries: {
      retry: (count, error) => !(error instanceof ApiRequestError) && count < 3,
      refetchOnWindowFocus: false,
    },
  },
});
const router = createAppRouter(queryClient);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
